<template>
  <v-container
    ref="container"
    v-scroll="onScroll"
    fluid
  >
    <template v-if="isLoading && !StoreMenu[id]">
      <template v-for="(category) in 2">
        <v-row :key="`category-${category}`">
          <v-col cols="12">
            <v-skeleton-loader
              class="py-4"
              max-width="900"
              type="heading"
            />
          </v-col>

          <v-col cols="12">
            <v-container
              fluid
              pa-0
            >
              <v-row>
                <template v-for="(product) in 5">
                  <v-col
                    :key="`category-${category}-product-${product}`"
                    cols="12"
                    lg="4"
                    md="6"
                  >
                    <div>
                      <v-skeleton-loader
                        :types="{'v-skeleton-product-list-item': 'article, avatar'}"
                        class="brand-beige-light"
                        type="v-skeleton-product-list-item"
                      />
                    </div>
                  </v-col>
                </template>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </template>
    </template>

    <template v-else>
      <v-row
        v-if="!storeCanSellItems(StoreInfo) || storeIsClosed(StoreInfo)"
        :dense="$vuetify.breakpoint.xsOnly"
      >
        <v-col
          v-if="storeIsClosed(StoreInfo)"
          cols="12"
        >
          <v-alert
            border="left"
            class="mb-0 pr-2 text-no-select text-sm-body-1 text-body-2"
            color="error"
            dense
            text
            type="error"
          >
            <template #prepend>
              <v-icon
                class="mr-2"
                color="error"
                size="28"
              >
                mdi-store-clock
              </v-icon>
            </template>

            {{ $t('Store.Status.ClosedNoOrders') }}
          </v-alert>
        </v-col>

        <v-col
          v-else-if="!storeCanSellItems(StoreInfo)"
          cols="12"
        >
          <v-alert
            border="left"
            class="mb-0 pr-2 text-no-select text-sm-body-1 text-body-2"
            color="warning"
            dense
            text
            type="warning"
          >
            <template #prepend>
              <v-icon
                v-if="cartOrderTypeIsDelivery && !storeHasDelivery(StoreInfo)"
                class="mr-2"
                color="warning"
                size="28"
              >
                mdi-moped
              </v-icon>

              <v-icon
                v-if="cartOrderTypeIsCollection && !storeHasCollection(StoreInfo)"
                class="mr-2"
                color="warning"
                size="28"
              >
                mdi-run
              </v-icon>
            </template>

            <template v-if="cartOrderTypeIsDelivery && !storeHasDelivery(StoreInfo)">
              {{ $t('Store.Status.NoDelivery') }}
            </template>

            <template v-if="cartOrderTypeIsCollection && !storeHasCollection(StoreInfo)">
              {{ $t('Store.Status.NoCollection') }}
            </template>
          </v-alert>
        </v-col>
      </v-row>

      <v-row
        v-if="AppConfig.Marketplace.TagsEnabled && !!AppTags.length"
        :dense="$vuetify.breakpoint.xsOnly"
        class="mt-4 mt-md-8"
      >
        <v-col cols="12">
          <h2 class="brand-text-heading">
            {{ toLocaleUpperCase($tc('Tag.Title', 0)) }}
          </h2>

          <v-divider
            class="mt-3"
            style="border-color: var(--v-brand-green-dark-base)"
          />
        </v-col>

        <template v-for="(tag, idx) in AppTags">
          <v-col
            :key="`tag-${idx}`"
            :class="idx === 0 ? 'pr-1' : 'px-1'"
            cols="auto"
          >
            <v-chip
              v-if="tag.Visible"
              :color="tag.Color"
              :dark="getContrastColor(tag.Color,168) === 'white'"
              :to="{name: 'Tag', params: {slug: getTranslatedField(tag, 'Slug'), id: tag.Id}}"
              class="px-4"
              label
              large
            >
              <v-icon
                v-if="tag.Icon && !tag.LabelOnly"
                left
              >
                {{ tag.Icon }}
              </v-icon>
              {{ getTranslatedField(tag, 'Label') }}
            </v-chip>
          </v-col>
        </template>
      </v-row>

      <template v-for="(item, catIdx) in storeMenuItems">
        <v-row
          v-if="item.Category.Status && item.Items.length > 0"
          :id="`tab-${getTranslatedField(item.Category, 'Slug')}`"
          :key="`category-${item.Category.Id}-${catIdx}`"
          :dense="$vuetify.breakpoint.xsOnly"
          class="mt-8 mt-md-16"
        >
          <v-col cols="12">
            <h2 class="brand-text-heading">
              {{ toLocaleUpperCase(getTranslatedField(item.Category)) }}
            </h2>

            <v-divider
              class="mt-3"
              style="border-color: var(--v-brand-green-dark-base)"
            />
          </v-col>

          <v-col cols="12">
            <v-container
              fluid
              pa-0
            >
              <v-row :dense="$vuetify.breakpoint.smAndDown">
                <template v-for="(product, prodIdx) in item.Items">
                  <v-col
                    v-if="product.Active"
                    :key="`category-${item.Category.Id}-${catIdx}-product-${product.Id}-${prodIdx}`"
                    cols="12"
                    lg="4"
                    md="6"
                  >
                    <catalog-product-list-item
                      :disabled="AppConfig.StoreClosed.DisableProducts && storeIsClosedById(id)"
                      :product="product"
                    />
                  </v-col>
                </template>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </template>
    </template>

    <v-row>
      <v-col
        class="py-10"
        cols="12"
      >
        <v-alert
          border="left"
          class="mb-0 pr-2 text-no-select text-sm-body-1 text-body-2"
          color="brand-primary darken-2"
          dense
          text
          type="info"
        >
          <template #prepend>
            <v-icon
              class="mr-2"
              color="brand-primary"
              size="28"
            >
              mdi-information
            </v-icon>
          </template>

          <h4>{{ $t('Store.Notify.Allergens.Title') }}</h4>
          
          <i18n
            path="Store.Notify.Allergens.Body"
            tag="div"
          >
            <router-link
              v-t="$t('Common.Button.Here')"
              :to="{name: 'PoliciesAllergens'}"
              class="text-lowercase"
            />
          </i18n>
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" />
    </v-row>

    <select-store-dialog
      :stores="AppStores"
      :visible.sync="storeDialogVisible"
      @store:selected="onStoreSelected"
    />
  </v-container>
</template>

<script>
import StoreMixin from '@/theme/default/views/store/Store.js'

export default {
  name  : 'Store',
  mixins: [StoreMixin]
}
</script>

<style scoped src="@/theme/default/views/store/Store.css"></style>

<style scoped>
/deep/ .theme--light.v-skeleton-loader .v-skeleton-loader__article {
  background-color: var(--v-brand-beige-light-base);
}
</style>
